import { useState, useEffect, useCallback } from 'react';
import { multiplierService, Multiplier } from '../services/multiplierService';
import { useGameState } from './useGameState';
import { gameDB } from '../services/db';
import toast from 'react-hot-toast';

export function useMultipliers(walletAddress: string | null) {
  const { gameState, updateGameState } = useGameState(walletAddress);
  const [multipliers, setMultipliers] = useState<Multiplier[]>([]);

  // Load multipliers
  useEffect(() => {
    setMultipliers(
      multiplierService.getMultipliers(
        gameState.level,
        gameState.multipliers || {}
      )
    );
  }, [gameState.level, gameState.multipliers]);

  // Save multipliers
  const saveMultipliers = useCallback(async () => {
    if (!walletAddress) return;
    try {
      await gameDB.savePurchases(walletAddress, {
        ...gameState,
        multipliers: gameState.multipliers || {}
      });
    } catch (error) {
      console.error('Error saving multipliers:', error);
    }
  }, [walletAddress, gameState]);

  // Buy multiplier
  const buyMultiplier = useCallback((multiplier: Multiplier, balance: number) => {
    if (!multiplier) return null;

    const { canBuy, reason } = multiplierService.canBuyMultiplier(
      multiplier,
      balance,
      gameState.level
    );

    if (!canBuy) {
      toast.error(reason || 'Cannot buy this multiplier');
      return null;
    }

    // Update multipliers
    const newMultipliers = {
      ...(gameState.multipliers || {}),
      [multiplier.id]: ((gameState.multipliers || {})[multiplier.id] || 0) + 1
    };

    // Calculate new coinsPerClick
    const totalMultiplier = multiplierService.calculateTotalMultiplier(newMultipliers);
    const newCoinsPerClick = Math.max(1, totalMultiplier); // Minimum 1 coin per click

    // Update game state
    updateGameState({
      multipliers: newMultipliers,
      coinsPerClick: newCoinsPerClick
    });

    saveMultipliers();
    toast.success(`Multiplier x${multiplier.type} purchased! Coins per click: ${newCoinsPerClick}`);
    
    return multiplier.currentPrice;
  }, [gameState, updateGameState, saveMultipliers]);

  // Calculate total multiplier
  const calculateTotalMultiplier = useCallback(() => {
    return multiplierService.calculateTotalMultiplier(gameState.multipliers || {});
  }, [gameState.multipliers]);

  return {
    multipliers,
    buyMultiplier,
    calculateTotalMultiplier
  };
}